import { Separator, Text } from '@cuemath/web-comps';
import {
  Button,
  Card,
  Dropdown,
  Icon,
  Input,
  Modal,
  ModalActions,
  ModalContent,
  ModalDescription,
  ModalHeader,
  TextArea,
} from 'semantic-ui-react';
import COLORS from '../../constants/colors';
import React from 'react';
import { CANCELLATION_REASONS } from './utils';
import { Col } from '@cuemath/comps';
import { DateTime } from 'luxon';

const CancelTrial = ({
  closeModal,
  onCancel,
  studentName,
  onAssignSlots,
  demoTime,
  timezone,
}) => {
  const [page, setPage] = React.useState(1);
  const [cancellationObj, setCancellationObj] = React.useState({
    cancellationReason: '',
    customReason: '',
  });
  const [followUpDate, setFollowUpDate] = React.useState(null);

  const handleNext = React.useCallback(() => {
    if (page === 2) {
      onCancel(cancellationObj);
      closeModal();

      return;
    }

    if (page === 4) {
      onAssignSlots({ followUpDate }, 'reschedule_later');
      closeModal();

      return;
    }
    setPage(prev => prev + 1);
  }, [cancellationObj, closeModal, onCancel, page, onAssignSlots, followUpDate]);

  const handleBack = React.useCallback(() => {
    setPage(prev => prev - 1);
  }, []);

  const handleReschedule = React.useCallback(() => {
    setPage(3);
  }, []);

  const handleDropdownChange = React.useCallback((e, { value }) => {
    setCancellationObj(prev => ({ ...prev, cancellationReason: value }));
  }, []);

  const handleTextAreaChange = React.useCallback((e, { value }) => {
    setCancellationObj(prev => ({ ...prev, customReason: value }));
  }, []);

  const handleChange = (e, { value }) => {
    setFollowUpDate(value);
  };

  const disabledNext = React.useMemo(() => {
    if (page === 1) {
      if (!cancellationObj.cancellationReason) {
        return true;
      }
      if (cancellationObj.cancellationReason === 'Other') {
        if (
          !cancellationObj.customReason ||
          cancellationObj.customReason.trim().length < 10
        ) {
          return true;
        }
      }
    }

    if (page === 3) {
      return !followUpDate;
    }

    return false;
  }, [page, cancellationObj, followUpDate]);

  const zonedDate = DateTime.fromSeconds(demoTime, { zone: timezone });
  const formattedDemoDate = zonedDate.toFormat("MMM d, yyyy 'at' HH:mm");
  const formattedFollowUpDate = DateTime.fromISO(followUpDate).toFormat('MMM dd, yyyy');

  return (
    <Modal open={true} onClose={closeModal}>
      <ModalHeader>
        {page === 3 || page === 4 ? (
          <>
            <Text variant="h6">Reschedule for Later</Text>
            <Text variant="body3" color={COLORS.GRAY}>
              Remove the current trial slot and set a follow-up date for {studentName}.
            </Text>
          </>
        ) : (
          <>
            <Text variant="h6">Cancel Trial</Text>
            <Text variant="body3" color={COLORS.GRAY}>
              Cancel the trial for {studentName}. This action cannot be undone.
            </Text>
          </>
        )}
      </ModalHeader>
      <ModalContent scrolling>
        <ModalDescription>
          {page === 1 && (
            <>
              <Text variant="body1Medium">Cancellation Reason</Text>
              <Separator height={8} transparent />
              <Dropdown
                floating
                fluid
                selection
                onChange={handleDropdownChange}
                options={CANCELLATION_REASONS}
                value={cancellationObj.cancellationReason}
                selectOnBlur={false}
                placeholder="Select reason for cancellation"
              />
              {cancellationObj.cancellationReason === 'Other' ? (
                <>
                  <Separator height={16} transparent />
                  <Text variant="body1Medium">Custom Reason</Text>
                  <Separator height={8} transparent />
                  <TextArea
                    placeholder="Please specify the reason for cancellation"
                    style={{ minHeight: 100, maxWidth: '100%', minWidth: '100%' }}
                    value={cancellationObj.customReason}
                    onChange={handleTextAreaChange}
                    fluid
                  />
                </>
              ) : (
                <Separator height={148} transparent />
              )}
            </>
          )}
          {page === 2 && (
            <>
              <Card color="red" fluid style={{ padding: '16px' }}>
                <Text variant="body2">Are you sure you want to cancel this trial?</Text>
                <Separator height={16} transparent />
                <Text variant="body2">
                  This action cannot be undone. The parent and tutor will be notified.
                </Text>
              </Card>
            </>
          )}
          {page === 3 && (
            <>
              <Text variant="body1Medium">
                When would you like to follow up with this lead?
              </Text>
              <Separator height={32} transparent />
              <Text variant="body1Regular">Follow-up Date</Text>
              <Separator height={8} transparent />
              <Input
                onChange={handleChange}
                type="date"
                min={new Date().toISOString().split('T')[0]}
                value={followUpDate}
              />
            </>
          )}
          {page === 4 && (
            <Col gutter={0}>
              <Text variant="h6">Please review the details before confirming.</Text>
              <Separator height={16} transparent />
              <Col
                gutter={16}
                gap={16}
                style={{ background: '#f2f1f1ff', borderRadius: '8px' }}
              >
                <Text variant="body1Medium">Current Trial Slot</Text>
                <Text variant="body1Regular">{formattedDemoDate}</Text>
                <Separator height={16} transparent />
                <Text variant="body1Medium">Follow-up Date</Text>
                <Text variant="body1Regular" color={COLORS.RUSTY_RED}>
                  {formattedFollowUpDate}
                </Text>
              </Col>
              <Separator height={16} transparent />
              <Col
                gutter={16}
                gap={16}
                style={{
                  background: 'rgb(255, 247, 237)',
                  borderRadius: '8px',
                  border: '1px solid rgb(254, 215, 170)',
                }}
              >
                <Text variant="body1Medium" color="#9A3412">
                  <Icon name="info circle" />
                  &nbsp;&nbsp; This action will:
                </Text>
                <Separator height={8} transparent />
                <Col gutter={36}>
                  <Text variant="body1Regular" color="#9A3412">
                    &bull;&nbsp;&nbsp;Remove the current trial slot
                  </Text>
                  <Separator height={4} transparent />
                  <Text variant="body1Regular" color="#9A3412">
                    &bull;&nbsp;&nbsp;Move the lead back to Qualified Lead status
                  </Text>
                  <Separator height={4} transparent />
                  <Text variant="body1Regular" color="#9A3412">
                    &bull;&nbsp;&nbsp;Set a follow-up date for future contact
                  </Text>
                </Col>
              </Col>
            </Col>
          )}
        </ModalDescription>
      </ModalContent>
      <ModalActions>
        {page === 1 && (
          <>
            <Button onClick={closeModal} basic>
              Cancel
            </Button>
            <Button onClick={handleReschedule} basic>
              Reschedule for Later
            </Button>
            <Button onClick={handleNext} color="red" disabled={disabledNext}>
              Cancel Trial
            </Button>
          </>
        )}
        {page === 2 && (
          <>
            <Button onClick={handleBack} basic>
              Keep Trial
            </Button>
            <Button onClick={handleNext} color="red">
              Yes, Cancel Trial
            </Button>
          </>
        )}
        {page === 3 && (
          <>
            <Button onClick={closeModal} basic>
              Cancel
            </Button>
            <Button onClick={handleNext} color="black" disabled={disabledNext}>
              Next
            </Button>
          </>
        )}
        {page === 4 && (
          <>
            <Button onClick={handleBack} basic>
              Previous
            </Button>
            <Button onClick={closeModal} basic>
              Cancel
            </Button>
            <Button onClick={handleNext} color="red">
              Confirm Reschedule for Later
            </Button>
          </>
        )}
      </ModalActions>
    </Modal>
  );
};

export default CancelTrial;
